/** @velyn-custom BEGIN — vely-product-icon-box.js
 *  NPS-style bleed + slidesOffsetBefore when carousel; pill pagination; grid fallback.
 *  Desktop/mobile carousel flags mirror vely-featured-collection.
 */
(function () {
  const getSwiper = () => window.Swiper;

  class VelyProductIconBox extends HTMLElement {
    /**
     * Cards stay between a floor and 520px, shrinking to fit .container before sliding.
     * Floor is 360px from 1440px up, and 400px below that.
     * If every card fits at the floor, the row stays inside the container.
     * If not, the track slides: first card aligns to the container, right side bleeds.
     */
    measureContainer() {
      const section = this.closest(
        ".section-vely-product-icon-box, .section__vely-product-icon-box"
      );
      if (!section) return null;

      const probe = document.createElement("div");
      probe.className = "container";
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "height:0;overflow:hidden;visibility:hidden;pointer-events:none;";
      section.appendChild(probe);
      const rect = probe.getBoundingClientRect();
      const style = getComputedStyle(probe);
      const padStart = parseFloat(style.paddingInlineStart) || 0;
      const padEnd = parseFloat(style.paddingInlineEnd) || 0;
      probe.remove();

      return {
        left: rect.left + padStart,
        width: Math.max(0, rect.width - padStart - padEnd),
      };
    }

    countThatFit(containerWidth, gap, minWidth) {
      if (containerWidth <= 0) return 1;
      let count = 0;
      while ((count + 1) * minWidth + count * gap <= containerWidth + 0.5) {
        count += 1;
      }
      return Math.max(count, 1);
    }

    applyLayout(swiper = this._swiper) {
      if (!swiper) return;

      const MIN = window.matchMedia("(min-width: 1440px)").matches ? 360 : 400;
      const MAX = 520;
      const gap = Number(this.dataset.spacing) || 16;
      const slides = swiper.slides?.length
        ? Array.from(swiper.slides)
        : Array.from(this.querySelectorAll(".swiper-slide"));
      const count = slides.length;
      if (!count) return;

      const box = this.measureContainer();
      const containerWidth = box?.width || 0;
      const fitCount = this.countThatFit(containerWidth, gap, MIN);
      const contained = count <= fitCount;
      const columns = contained ? count : fitCount;
      let cardW =
        (containerWidth - Math.max(0, columns - 1) * gap) / Math.max(columns, 1);

      if (!Number.isFinite(cardW) || cardW <= 0) cardW = MIN;
      cardW = Math.min(MAX, cardW);
      if (containerWidth >= MIN) {
        cardW = Math.max(MIN, cardW);
      } else if (containerWidth > 0) {
        cardW = containerWidth;
      }
      cardW = Math.round(cardW);

      this.classList.toggle("vely-pib--contained", contained);
      this.classList.toggle("vely-pib--slide", !contained);
      this.classList.add("vely-pib--sized");
      this.style.setProperty("--vely-pib-card-w", cardW + "px");

      slides.forEach((slide) => {
        slide.style.width = cardW + "px";
      });

      const shell = this.querySelector(".vely-pib__slider-shell");
      const shellLeft = shell ? shell.getBoundingClientRect().left : 0;
      const before = contained
        ? 0
        : Math.max(0, Math.round((box?.left || 0) - shellLeft));

      swiper.params.slidesPerView = "auto";
      swiper.params.spaceBetween = gap;
      swiper.params.slidesOffsetBefore = before;
      swiper.params.slidesOffsetAfter = 0;

      const shouldSlide = !contained && count > 1;
      if (swiper.enabled === false) swiper.enable();
      if (!shouldSlide) swiper.slideTo?.(0, 0);
      swiper.update();
      if (!shouldSlide) swiper.disable();

      this.syncControls(swiper);
    }

    connectedCallback() {
      if (this._velyPibInitStarted) return;
      this._velyPibInitStarted = true;
      this.waitForSwiper(() => this.init());
    }

    disconnectedCallback() {
      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }
      this._velyPibInitStarted = false;
      this._paginationEl = null;
      this.classList.remove("vely-pib--ready");
    }

    waitForSwiper(callback, attempt = 0) {
      const hasSwiper = typeof getSwiper() === "function";
      const needsPagination = this.isPaginationEnabled();
      const hasPagination =
        !needsPagination || Boolean(window.VelySwiperPagination?.getConfig);
      if (hasSwiper && hasPagination) {
        callback();
        return;
      }
      if (attempt > 80) {
        console.warn(
          "[vely-product-icon-box] Swiper or VelySwiperPagination not available"
        );
        return;
      }
      setTimeout(() => this.waitForSwiper(callback, attempt + 1), 50);
    }

    isPaginationEnabled() {
      return this.dataset.showPagination !== "false";
    }

    /**
     * Hide pagination when carousel is off for viewport or all cards fit.
     */
    syncControls(swiper) {
      if (!this.isPaginationEnabled()) return;

      const controls = this.querySelector("[data-vely-pagination-anchor]");
      if (!controls || !swiper) return;

      window.VelySwiperPagination?.refresh?.(swiper, this._paginationEl);

      const carouselActive = this.classList.contains("vely-pib--slide");
      if (!carouselActive || swiper.enabled === false) {
        controls.hidden = true;
        controls.setAttribute("aria-hidden", "true");
        return;
      }

      swiper.update?.();
      const minT =
        typeof swiper.minTranslate === "function" ? swiper.minTranslate() : 0;
      const maxT =
        typeof swiper.maxTranslate === "function" ? swiper.maxTranslate() : 0;
      const travel = Math.abs(maxT - minT);
      const snaps = swiper.snapGrid?.length ?? 0;
      const canMove = Boolean(swiper.allowSlideNext || swiper.allowSlidePrev);
      const canSlide = !swiper.isLocked && canMove && snaps > 1 && travel > 1;

      controls.hidden = !canSlide;
      controls.setAttribute("aria-hidden", canSlide ? "false" : "true");
    }

    init() {
      const SwiperCtor = getSwiper();
      if (typeof SwiperCtor !== "function") return;

      const swiperEl = this.querySelector(".vely-pib__swiper");
      if (!swiperEl) return;

      if (this._swiper) {
        this._swiper.destroy(true, true);
        this._swiper = null;
      }

      const gap = Number(this.dataset.spacing) || 16;
      const useLoop = this.dataset.loop === "true";

      this._paginationEl = this.isPaginationEnabled()
        ? this.querySelector(".vely-swiper-pagination-pill")
        : null;

      const paginationConfig =
        this._paginationEl && window.VelySwiperPagination?.getConfig
          ? window.VelySwiperPagination.getConfig(this)
          : null;

      const swiperConfig = {
        slidesPerView: "auto",
        spaceBetween: gap,
        speed: 300,
        loop: useLoop,
        grabCursor: true,
        watchSlidesProgress: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        on: {
          init: (swiper) => {
            if (this._paginationEl) {
              window.VelySwiperPagination?.pinInFlow(this._paginationEl);
            }
            this.applyLayout(swiper);
          },
          resize: (swiper) => {
            this.applyLayout(swiper);
          },
          slidesLengthChange: (swiper) => {
            this.syncControls(swiper);
          },
          slideChange: (swiper) => {
            if (
              !this.isPaginationEnabled() ||
              !this._paginationEl ||
              !this.classList.contains("vely-pib--slide")
            ) {
              return;
            }
            window.VelySwiperPagination?.setActiveBullet?.(
              swiper,
              this._paginationEl
            );
          },
        },
      };

      if (paginationConfig) {
        swiperConfig.pagination = paginationConfig;
      }

      try {
        this._swiper = new SwiperCtor(swiperEl, swiperConfig);
      } catch (error) {
        this._swiper = null;
        console.warn("[vely-product-icon-box] Swiper init failed", error);
        return;
      }

      if (this._paginationEl) {
        window.VelySwiperPagination?.pinInFlow(this._paginationEl);
      }
      requestAnimationFrame(() => this.applyLayout(this._swiper));
      this.syncControls(this._swiper);
      this.classList.add("vely-pib--ready");
    }
  }

  if (!customElements.get("vely-product-icon-box")) {
    customElements.define("vely-product-icon-box", VelyProductIconBox);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const host = event.target?.querySelector?.("vely-product-icon-box");
    if (host) {
      host._velyPibInitStarted = false;
      host.connectedCallback();
    }
  });
})();
/** @velyn-custom END — vely-product-icon-box.js */
